import { useState, useEffect } from 'react'
import Header from '../components/Header'
import PricingCard from '../components/PricingCard'
import { api } from '../services/api'
import toast from 'react-hot-toast'

interface PricingTier {
  id: string
  name: string
  price_per_user: number
  interval: string
  features: string[]
  stripe_price_id: string
}

const PricingPage: React.FC = () => {
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await api.get('/pricing')
        setPricingTiers(response.data.pricing)
      } catch (error) {
        toast.error('Failed to load pricing information')
        // Fallback pricing data
        setPricingTiers([
          {
            id: "monthly",
            name: "InlandChat Monthly",
            price_per_user: 100,
            interval: "month",
            stripe_price_id: "price_1SLK0AEAI04pJvVrifwQEQvP",
            features: [
              "Unlimited AI conversations",
              "Access to all AI models",
              "File uploads",
              "Priority support",
            ],
          },
          {
            id: "yearly",
            name: "InlandChat Yearly",
            price_per_user: 9599,
            interval: "year",
            stripe_price_id: "price_1SLGfhEAI04pJvVrNtAnmi2l",
            features: [
              "Unlimited AI conversations",
              "Access to all AI models",
              "File uploads",
              "Priority support",
              "Save 20% with yearly billing",
            ],
          },
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchPricing()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
        <Header />
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2" style={{borderColor: '#3A9E5A'}}></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
      <Header />
      
      <main className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{color: '#f9fafb'}}>
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl max-w-2xl mx-auto" style={{color: '#d1d5db'}}>
              Choose the plan that works best for you. All plans include unlimited access to LibreChat.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <PricingCard
                key={tier.name}
                tier={tier}
                isPopular={index === 1} // Yearly plan is popular
              />
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-center mb-12" style={{color: '#f9fafb'}}>
              Frequently Asked Questions
            </h2>
            
            <div className="max-w-3xl mx-auto space-y-8">
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{color: '#f9fafb'}}>
                  What's included in the subscription?
                </h3>
                <p style={{color: '#d1d5db'}}>
                  All subscriptions include unlimited AI conversations, access to all available AI models, 
                  file upload capabilities, and priority customer support.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2" style={{color: '#f9fafb'}}>
                  Can I cancel anytime?
                </h3>
                <p style={{color: '#d1d5db'}}>
                  Yes, you can cancel your subscription at any time. Your access will continue until the 
                  end of your current billing period.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2" style={{color: '#f9fafb'}}>
                  Is there a free trial?
                </h3>
                <p style={{color: '#d1d5db'}}>
                  Yes! You can register for free and explore the platform. A subscription is required 
                  for full access to all features.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-2" style={{color: '#f9fafb'}}>
                  What payment methods do you accept?
                </h3>
                <p style={{color: '#d1d5db'}}>
                  We accept all major credit cards through our secure Stripe payment processing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default PricingPage
